import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { companyInfo } from "@/mocks/websiteData";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 30);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Services" },
    { to: "/products", label: "Products" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-lg shadow-md shadow-black/5 border-b border-background-200/60"
          : "bg-black/30 backdrop-blur-sm border-b border-white/10"
      }`}
    >
      <div className="section-padding">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Logo + Brand Name */}
          <Link to="/" className="flex items-center gap-3 shrink-0 group">
            <div className="rounded-full p-1 transition-all duration-300 group-hover:scale-105">
              <img
                src="https://static.readdy.ai/image/b8a021112837a070edfa44e380da93b7/afc6607cdb23371748ceab61c8f87948.png"
                alt="NexSunNexova Solar Energy Logo"
                className="h-14 w-auto object-contain rounded-full md:h-16"
              />
            </div>
            <div className="hidden sm:flex flex-col">
              <span className={`text-lg font-bold leading-tight tracking-tight drop-shadow-md md:text-xl transition-colors duration-300 ${scrolled ? 'text-foreground-900' : 'text-white'}`}>
                NexSunNexova
              </span>
              <span className={`text-[10px] font-medium uppercase tracking-wider leading-tight md:text-xs transition-colors duration-300 ${scrolled ? 'text-foreground-500' : 'text-white/80'}`}>
                Solar Energy Pvt. Ltd.
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1 bg-white/5 backdrop-blur-sm rounded-full px-2 py-1.5 border border-white/10">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 ${
                  isActive(link.to)
                    ? scrolled
                      ? "text-primary-600 bg-primary-50 shadow-sm"
                      : "text-white bg-white/20 shadow-sm"
                    : scrolled
                      ? "text-foreground-600 hover:text-primary-600 hover:bg-primary-50/60"
                      : "text-white/80 hover:text-white hover:bg-white/15"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA + Mobile Toggle */}
          <div className="flex items-center gap-3">
            <a
              href="tel:8019051311"
              className={`hidden sm:flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all duration-300 shadow-lg ${
                scrolled
                  ? "bg-primary-500 text-background-50 hover:bg-primary-600 shadow-primary-500/25"
                  : "bg-white/90 text-foreground-900 hover:bg-white shadow-white/20"
              }`}
            >
              <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
              <span>Get Free Quote</span>
            </a>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`lg:hidden p-2.5 rounded-xl transition-all duration-300 ${
                scrolled
                  ? "text-foreground-700 hover:bg-background-100"
                  : "text-white hover:bg-white/15"
              }`}
              aria-label="Toggle menu"
            >
              <i className={`ri-${mobileOpen ? "close" : "menu-3"}-line w-5 h-5 flex items-center justify-center text-xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-500 ease-in-out ${
          mobileOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="section-padding py-5 bg-background-50/98 backdrop-blur-xl border-t border-background-200/60">
          <div className="flex flex-col gap-1.5">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                  isActive(link.to)
                    ? "text-primary-600 bg-primary-50"
                    : "text-foreground-700 hover:text-primary-600 hover:bg-primary-50/40"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <a
              href="tel:8019051311"
              className="mt-3 flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors shadow-lg shadow-primary-500/20"
            >
              <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
              <span>Get Free Quote — 8019051311</span>
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
}